import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import api from "@/services/api";

// =====================================================================
// Shared terrain map: same visuals on Find-a-terrain and Create-match.
// =====================================================================

const PRIVATE_KEYWORDS = [
  "five", "urban", "soccerpark", "sportingpark",
  "soccer park", "soccer5", "soccer", "kipstadium", "z5",
];

export function isPaidTerrain(t) {
  if (t.is_paid === true) return true;
  if (t.type === "Prive" || t.type === "Club") return true;
  const n = (t.nom || "").toLowerCase();
  return PRIVATE_KEYWORDS.some((k) => n.includes(k));
}

export const STADIUM_SVG = `
<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <ellipse cx="12" cy="12" rx="9" ry="5.5" />
  <line x1="12" y1="6.5" x2="12" y2="17.5" />
  <circle cx="12" cy="12" r="1.6" fill="#fff" stroke="none"/>
</svg>`;

function makeTerrainIcon(paid) {
  const size = 30;
  const totalW = size + (paid ? 12 : 4);
  const totalH = size + (paid ? 12 : 4);
  const euroBadge = paid
    ? `<div style="
         position:absolute; top:-4px; right:-4px;
         width:16px; height:16px; border-radius:50%;
         background:#fff; color:#16a34a; font-weight:900; font-size:10px;
         display:flex; align-items:center; justify-content:center;
         border:2px solid #16a34a; box-shadow:0 1px 3px rgba(0,0,0,.4);
         line-height:1;
       ">€</div>`
    : "";
  return new L.DivIcon({
    className: "matchup-terrain-pin",
    html: `
      <div style="position:relative; width:${size}px; height:${size}px;">
        <div style="
          width:${size}px; height:${size}px; border-radius:50%;
          background:linear-gradient(135deg,#22c55e 0%,#16a34a 100%);
          border:2.5px solid #fff;
          box-shadow:0 3px 8px rgba(0,0,0,.55);
          display:flex; align-items:center; justify-content:center;
        ">
          ${STADIUM_SVG}
        </div>
        ${euroBadge}
      </div>`,
    iconSize: [totalW, totalH],
    iconAnchor: [size / 2, size - 2],
    popupAnchor: [0, -size + 4],
  });
}

export const FREE_ICON = makeTerrainIcon(false);
export const PAID_ICON = makeTerrainIcon(true);

export const MY_POSITION_ICON = new L.DivIcon({
  className: "matchup-me",
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#3b82f6;border:3px solid #fff;box-shadow:0 0 0 6px rgba(59,130,246,0.25);"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// ---------- Inner Leaflet helpers ----------

function MarkersClusterLayer({ terrains, onTerrainClick }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 50,
      iconCreateFunction: (clusterObj) => {
        const count = clusterObj.getChildCount();
        const size = count < 10 ? 38 : count < 100 ? 46 : 56;
        return L.divIcon({
          html: `<div style="
            width:${size}px;height:${size}px;border-radius:50%;
            background:rgba(34,197,94,0.92);color:#fff;
            display:flex;align-items:center;justify-content:center;
            font-weight:800;font-size:14px;font-family:Inter,sans-serif;
            border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4);
          ">${count}</div>`,
          className: "matchup-cluster",
          iconSize: [size, size],
        });
      },
    });

    terrains.forEach((t) => {
      const coords = t.coordonnees?.coordinates;
      if (!coords) return;
      const [lng, lat] = coords;
      const icon = isPaidTerrain(t) ? PAID_ICON : FREE_ICON;
      const marker = L.marker([lat, lng], { icon });
      marker.on("click", () => onTerrainClick(t));
      cluster.addLayer(marker);
    });

    map.addLayer(cluster);
    layerRef.current = cluster;
    return () => {
      if (layerRef.current) map.removeLayer(layerRef.current);
    };
  }, [terrains, map, onTerrainClick]);

  return null;
}

function BoundsTracker({ onBoundsChanged }) {
  const map = useMap();
  useEffect(() => {
    const handler = () => {
      const b = map.getBounds();
      onBoundsChanged({
        minLat: b.getSouth(),
        minLng: b.getWest(),
        maxLat: b.getNorth(),
        maxLng: b.getEast(),
      });
    };
    handler();
    map.on("moveend", handler);
    return () => map.off("moveend", handler);
  }, [map, onBoundsChanged]);
  return null;
}

export function RecenterControl({ position, instant }) {
  const map = useMap();
  useEffect(() => {
    if (!position) return;
    if (instant) {
      map.setView(position, 14, { animate: false });
    } else {
      map.flyTo(position, 14, { duration: 0.6 });
    }
  }, [position, instant, map]);
  return null;
}

// ---------- Public component ----------

/**
 * <TerrainMap>: single source of truth for the clustered terrain map used
 * across Find-a-terrain (`/find`) and the Create-match dialog.
 *
 * Props:
 *  - center: [lat, lng] required. Initial map center.
 *  - zoom: number (default 13).
 *  - terrains: list of pre-fetched terrains. When provided, the component
 *      renders this exact list (parent owns filtering/fetching).
 *  - loadByBbox: when true AND `terrains` is omitted, the map auto-fetches
 *      terrains in viewport on moveend. Ignored if `terrains` is provided.
 *  - onTerrainClick: (terrain) => void  fired when a marker is tapped.
 *  - onBoundsChanged: (bbox) => void  emitted on moveend whenever provided,
 *      so the parent can run custom fetching/filtering (e.g. paid/free).
 *  - onTerrainsLoaded: (list) => void  fires after each bbox auto-fetch.
 *  - showMyPosition: when true, render a blue dot at `center`.
 *  - recenterTrigger: bump to recenter the map (animated).
 *  - height: CSS height string (default "100%").
 */
export default function TerrainMap({
  center,
  zoom = 13,
  terrains: terrainsProp,
  loadByBbox = true,
  onTerrainClick,
  onBoundsChanged,
  onTerrainsLoaded,
  showMyPosition = false,
  recenterTrigger = 0,
  height = "100%",
}) {
  const lastBboxRef = useRef(null);
  const [localTerrains, setLocalTerrains] = useState(terrainsProp || []);

  const handleBbox = async (bbox) => {
    if (onBoundsChanged) onBoundsChanged(bbox);
    // Parent owns terrains list: skip internal fetching.
    if (terrainsProp) return;
    // Default behaviour: auto-fetch by bbox, with a small dedupe.
    if (!loadByBbox) return;
    const last = lastBboxRef.current;
    if (last
      && Math.abs(last.minLat - bbox.minLat) < 0.001
      && Math.abs(last.maxLat - bbox.maxLat) < 0.001
      && Math.abs(last.minLng - bbox.minLng) < 0.001
      && Math.abs(last.maxLng - bbox.maxLng) < 0.001) return;
    lastBboxRef.current = bbox;
    try {
      const data = await api.terrainsBbox({ ...bbox, limit: 1500 });
      setLocalTerrains(data || []);
      if (onTerrainsLoaded) onTerrainsLoaded(data || []);
    } catch {
      /* network errors are non-fatal here */
    }
  };

  const renderTerrains = terrainsProp ?? localTerrains;
  const trackBounds = Boolean(onBoundsChanged) || (loadByBbox && !terrainsProp);

  // react-leaflet's <MapContainer> only forwards a whitelist of props/attrs
  // (className, style, id, role, aria-*) to the rendered div. Arbitrary
  // data-* attributes are dropped, so we wrap with our own div to expose
  // the testid for e2e tooling.
  return (
    <div data-testid="terrain-map" style={{ height, width: "100%" }}>
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom
        zoomControl={false}
        attributionControl={false}
        zoomSnap={0.5}
        zoomDelta={0.5}
        wheelDebounceTime={40}
        wheelPxPerZoomLevel={80}
        style={{ height: "100%", width: "100%", backgroundColor: "#0a0a0a" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution=""
        />
        <RecenterControl position={center} instant key={recenterTrigger} />
        {trackBounds && <BoundsTracker onBoundsChanged={handleBbox} />}
        <MarkersClusterLayer
          terrains={renderTerrains}
          onTerrainClick={onTerrainClick || (() => {})}
        />
        {showMyPosition && center && (
          <>
            <Marker position={center} icon={MY_POSITION_ICON} />
            <Circle
              center={center}
              radius={120}
              pathOptions={{
                color: "#3b82f6",
                fillColor: "#3b82f6",
                fillOpacity: 0.08,
                weight: 1,
              }}
            />
          </>
        )}
      </MapContainer>
    </div>
  );
}
